<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { useFormValidation } from "../../../../composables/useFormValidation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { required } from "../../../../utils/validation";
import {
  ChartOfAccount,
  Currency,
  Customer,
  GeneralLedger,
  Item,
  Outlet,
  PaymentType,
  RecordStatus,
  SalesRep,
  Warehouse,
} from "../../../../types/models";
import {
  documentActions,
  downloadPDFBlob,
  formatDate,
  formatNumber,
  isEmpty,
  printPDFBlob,
  sharePDFBlob,
} from "../../../../utils/CommonFunctions";
import MessageBox from "../../../../services/MessageBox";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";
import DateInput from "../../../../components/ui/form/DateInput.vue";
import RadioInput from "../../../../components/ui/form/RadioInput.vue";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";
import { useScreenSize } from "../../../../utils/useScreenSize";
import { tokenManager } from "../../../../api/utils/tokenManager";
import ItemSalesGrid from "../../../../components/ui/Templates/ItemSalesGrid.vue";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import TabComponent from "../../../../components/ui/form/TabComponent.vue";
import DeliverySteps from "../../../../components/ui/Templates/DeliverySteps.vue";
import TextAreaInput from "../../../../components/ui/form/TextAreaInput.vue";
import AccountingLog from "../../../../components/ui/Templates/AccountingLog.vue";
import Payments from "../../../../components/ui/Templates/Payments.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import SelectSalesReps from "../../../../components/ui/Templates/SelectSalesReps.vue";
import IconComponent from "../../../../components/ui/common/IconComponent.vue";
import FileUpload from "../../../../components/ui/form/FileUpload.vue";
import TextInput from "../../../../components/ui/form/TextInput.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  recordStatus?: RecordStatus[];
  cartItems?: Item[];
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save"): void;
  (e: "viewDocument", data: any, title: string, routePath: string): void;
}>();

const { isMobile } = useScreenSize();
const isReadOnly = ref<boolean>(props.mode === "view");

const mobileTabs = [
  { name: "customer", label: "Customer" },
  { name: "delivery", label: "Delivery" },
  { name: "document", label: "Summary" },
];

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  sales_quotations_id: props.data?.sales_quotations_id || "",
  date_time: props.data?.date_time || new Date().toISOString().split("T")[0],
  due_date: props.data?.due_date || new Date().toISOString().split("T")[0],
  customer: props.data?.customer,
  document_no: props.data?.customer || "",
  customers_id: props.data?.customers_id || "",
  prefix: props.data?.prefix || "",
  ref_no: props.data?.ref_no || "",
  uid: props.data?.uid || "",
  action: "",
  warehouses_id:
    props.data?.warehouses_id || tokenManager.getSetting("default_warehouse"),
  price_lists_id:
    props.data?.price_lists_id || tokenManager.getSetting("default_price_list"),
  credit_terms_id:
    props.data?.credit_terms_id ||
    tokenManager.getSetting("default_credit_term"),
  customer_reference: props.data?.customer_reference || "",
  remarks: props.data?.remarks || "",
  delivery_remarks: props.data?.delivery_remarks || "",
  status_id: props.data?.status_id || "",
  sales_orders_id: props.data?.sales_orders_id || "",
  sales_goods_deliverys_id: props.data?.sales_goods_deliverys_id || "",
  payment_types_id: props.data?.payment_types_id || "",
  sales_channels_id:
    props.data?.sales_channels_id ||
    tokenManager.getSetting("default_sales_channel"),
  outlets_id: props.data?.outlets_id || "",
  delivery_date:
    props.data?.delivery_date || new Date().toISOString().split("T")[0],
  delivery_address_id: props.data?.delivery_address_id || "",
  delivery_address: props.data?.delivery_address || "",
  sales_reps_id: props.data?.sales_reps_id || "",
  shift_end_reports_id: props.data?.shift_end_reports_id || "",
  outlet_pos_stations_id: props.data?.outlet_pos_stations_id || "",
  attachment: props.data?.attachment || "",
  chart_of_accounts_id: props.data?.chart_of_accounts_id || "",
  payment_reference: props.data?.payment_reference || "",
  payment_attachment: props.data?.payment_attachment || "",
  to_deliver: props.data?.to_deliver || false,
  items: props.data?.items || ([] as Item[]),
  accounting: props.data?.accounting || ([] as GeneralLedger[]),
  currencys_id: props.data?.currencys_id || 1,
  exchange_rate: props.data?.exchange_rate || 1,
  delivery_tracking: props.data?.delivery_tracking || "",
  credit_term: props.data?.credit_term || "",
  payments: props.data?.payments || [],
  quotation: null as any,
  download: false,
  order: null as any,
  delivery: null as any,
  credit_note: null as any,
});

const customer = ref<Customer>({} as Customer);
const warehouse = ref<Warehouse>();
const showSelectSalesReps = ref(false);

setFieldRules("customers_id", [required("Select Customer")]);
setFieldRules("warehouses_id", [required("Select Warehouse")]);
setFieldRules("price_lists_id", [required("Select Price List")]);
setFieldRules("credit_terms_id", [required("Payment Term is required")]);
setFieldRules("sales_channels_id", [required("Sales Channel is required")]);
setFieldRules("payment_types_id", [required("Payment Type is required")]);

const setData = (data: any) => {
  setFormData(data);
  fields.value.date_time.value = formatDate(data.date_time);
  fields.value.due_date.value = formatDate(data.due_date);
  fields.value.delivery_date.value = !isEmpty(data.delivery_date)
    ? formatDate(data.delivery_date)
    : "";
  customer.value = data.customer;
};

const getRecord = async () => {
  if (isEmpty(props.data)) return;
  const params = {
    id: props.data.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    params
  );
  if (response.success == true) {
    setData(response.data);
  }
};

const validateGrid = async (): Promise<boolean> => {
  for (const item of formData.value.items) {
    if (item.tracked_by === "Batch") {
      const batch_qty = item.batches?.reduce(
        (sum: number, batch: { quantity: number }) => {
          if (typeof batch.quantity === "number") {
            return sum + batch.quantity;
          }
          return sum;
        },
        0
      );
      if (batch_qty !== item.inventory_qty) {
        return false;
      }
    } else if (item.tracked_by === "Serial") {
      const serial_qty = item.serials?.reduce(
        (sum: number, serial: { quantity: number }) => {
          if (typeof serial.quantity === "number") {
            return sum + serial.quantity;
          }
          return sum;
        },
        0
      );
      if (serial_qty !== item.inventory_qty) {
        return false;
      }
    }
    if (item.shelfs && item.shelfs.length > 0) {
      let shelf_qty = item.shelfs?.reduce(
        (sum: number, shelf: { quantity: number }) => {
          if (typeof shelf.quantity === "number") {
            return sum + shelf.quantity;
          }
          return sum;
        },
        0
      );
      if (shelf_qty !== item.inventory_qty) {
        console.log(shelf_qty + " ==>> " + item.inventory_qty);
        return false;
      }
    }
  }
  return true;
};

const handleActions = async (action: string) => {
  documentActions(
    props.endPoint,
    formData.value.uid,
    action,
    "Sales Invoice# " + formData.value.document_no
  );
};

const handleSubmit = async (
  new_record: boolean,
  download: boolean = false,
  mail: boolean = false,
  share: boolean = false,
  print = false
) => {
  if (validateForm()) {
    const isValid = await validateGrid();
    if (!isValid) {
      MessageBox.show(
        "There are some Batch/Serial Quantity differences",
        "warning"
      );
      return;
    }
    let items = formData.value.items.filter((e: any) => e.items_id != "");
    if (items.length <= 0) {
      MessageBox.show("Select at least 1 item", "warning");
      return;
    }
    const totalNetAmount = formData.value.items.reduce(
        (sum: number, item: any) => {
          return sum + (Number(item.net_total) || 0);
        },
        0
      );
    if (payment_type.value?.check_credit) {
      const creditLimit = Number(customer?.value?.credit_limit || 0);
      const outstanding = Number(customer?.value?.outstanding || 0);
      console.log(creditLimit - outstanding);
      console.log(formData.value.items);
      
      console.log(totalNetAmount);
      if (creditLimit - outstanding < totalNetAmount) {
        MessageBox.show("Customer credit limit exceeded", "warning");
        return;
      }
    }
    formData.value.download = download || share || print;
    const payment = {
      amount: totalNetAmount,
      attachment: formData.value.payment_attachment,
      reference: formData.value.payment_reference,
      change: 0,
      currencys_id: formData.value.currencys_id,
      customers_id: formData.value.customers_id,
      exchange_rate: formData.value.exchange_rate,
      payment_types_id: formData.value.payment_types_id,
      payment_type: paymentTypes.value.find(
        (pt) => pt.id == formData.value.payment_types_id
      ),
      tendered: 0,
    }
    console.log('payment', payment);
    formData.value.payments = [payment];

    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "create",
      formData.value
    );
    if (response.success == true) {
      isReadOnly.value = true;
      Toast.show("saved successfully");
      emit("save");
      if (new_record) {
        clearForm();
      } else if (download) {
        downloadPDFBlob(
          response.data.file,
          "Sales Invoice #: " + response.data.data.document_no
        );
      } else if (mail) {
        if (!response.data.error) {
          Toast.show(
            "Sales Invoice #: " +
              response.data.data.document_no +
              " emailed successfully"
          );
        }
      } else if (share) {
        sharePDFBlob(
          response.data.file,
          "Sales Invoice #: " + response.data.data.document_no
        );
      } else if (print) {
        printPDFBlob(response.data.file);
      }
      setFormData(response.data.data);
      customer.value = response.data.customer;
    }
  }
};

const salesChannels = ref<any[]>([]);
const handleSalesChannelLoaded = (data: any) => {
  salesChannels.value = data;
  if (props.mode == "create") {
    fields.value.sales_channels_id.value = tokenManager.getSetting(
      "default_sales_channel",
      true
    );
    const defaultChannel = data.find(
      (item: any) => item.id == tokenManager.getSetting("default_sales_channel")
    );
    if (defaultChannel) {
      fields.value.to_deliver.value = defaultChannel.require_delivery == true;
    }
  }
};
const clearForm = () => {
  resetForm();
  fields.value.date_time.value = formatDate(new Date());
  fields.value.due_date.value = formatDate(new Date());
  fields.value.warehouses_id.value = tokenManager.getSetting(
    "default_warehouse",
    true
  );
  fields.value.price_lists_id.value =
    tokenManager.getSetting("default_price_list");
  fields.value.credit_terms_id.value = tokenManager.getSetting(
    "default_credit_term"
  );
  fields.value.sales_channels_id.value = tokenManager.getSetting(
    "default_sales_channel"
  );

  fields.value.items.value = [];

  customer.value = {} as Customer;
  isReadOnly.value = false;

  if (itemGridRef.value) {
    itemGridRef.value.addItemRow(-1);
  }

  nextTick(() => {
    const input = document.getElementById("customers_id");
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
};

const itemGridRef = ref<InstanceType<typeof ItemSalesGrid> | null>(null);
const handleNewItemRow = () => {
  if (itemGridRef.value) {
    itemGridRef.value.addItemRow(-1);
  }
};

const allowNewItemRow = ref(false);
const handleCustomer = (data: Customer) => {
  customer.value = data;
  if (!isEmpty(data.price_lists_id))
    fields.value.price_lists_id.value = data.price_lists_id;
  if (!isEmpty(data.credit_terms_id))
    fields.value.credit_terms_id.value = data.credit_terms_id;
  if (!isEmpty(data.sales_reps_id))
    fields.value.sales_reps_id.value = data.sales_reps_id;

  if (fields.value.warehouses_id.value == "")
    fields.value.warehouses_id.value = tokenManager.getSetting(
      "default_warehouse",
      true
    );
  if (fields.value.sales_channels_id.value == "")
    fields.value.sales_channels_id.value = tokenManager.getSetting(
      "default_sales_channel",
      true
    );
  if (
    customer.value &&
    customer.value.addresses &&
    customer.value.addresses.length > 0
  ) {
    fields.value.delivery_address_id.value = customer.value.addresses[0].id;
    fields.value.delivery_address.value =
      customer.value.addresses[0].full_address;
  }
  prepareItemRow();
};

const handleWarehouse = (data: any) => {
  warehouse.value = data;
  prepareItemRow();
};

const updateSalesRep = async (rep: SalesRep) => {
  if (!rep) return;
  let params = {
    id: formData.value?.uid,
    sales_reps_id: rep.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "assign-sales-rep",
    params
  );
  if (response.success == true) {
    setFormData(response.data);
    Toast.show("Sales Rep Assigned", "info");
    showSelectSalesReps.value = false;
  } else {
    Toast.show("No Sales Reps found", "warning");
  }
};

const handlePaymetTerms = (data: any) => {
  console.log(data);
  if (data.credit_days > 0) {
    const today = new Date();
    today.setDate(today.getDate() + data.credit_days);
    fields.value.due_date.value = formatDate(today);
    const pt = paymentTypes.value.find((pt) => pt.code == "CREDIT");
    if (pt) {
      fields.value.payment_types_id.value = pt.id;
      fields.value.chart_of_accounts_id.value = pt.chart_of_accounts_id;
    } else {
      fields.value.payment_types_id.value = "";
      fields.value.chart_of_accounts_id.value = "";
    }
  } else {
    fields.value.due_date.value = formatDate(new Date());
    const pt = paymentTypes.value.find((pt) => pt.code == data.name);
    if (pt) {
      fields.value.payment_types_id.value = pt.id;
      fields.value.chart_of_accounts_id.value = pt.chart_of_accounts_id;
    } else {
      fields.value.payment_types_id.value = "";
      fields.value.chart_of_accounts_id.value = "";
    }
  }
  prepareItemRow();
};

const payment_type = ref<PaymentType>();
const handlePaymetType = (data: any) => {
  payment_type.value = data;
  if (data) {
    fields.value.chart_of_accounts_id.value = data.chart_of_accounts_id;
    if (data.require_attachment) {
      setFieldRules("payment_attachment", [required("Attachment Required")]);
    } else {
      setFieldRules("payment_attachment", []);
    }
    if (data.require_reference) {
      setFieldRules("payment_reference", [required("Reference Required")]);
    } else {
      setFieldRules("payment_reference", []);
    }
  }
};

const paymentTypes = ref<PaymentType[]>([]);
const paymentTypesLoaded = (data: any[]) => {
  paymentTypes.value = data;
  console.log("credit_term", fields.value.credit_terms_id.value);
  console.log("paymentTerms", paymentTerms.value);
  const ct = paymentTerms.value.find(
    (ct) => ct.id == fields.value.credit_terms_id.value
  );
  console.log("ct", ct);
  if (ct) {
    const pt = paymentTypes.value.find((pt) => pt.code == ct.name);
    if (pt) {
      fields.value.payment_types_id.value = pt.id;
      fields.value.chart_of_accounts_id.value = pt.chart_of_accounts_id;
    } else {
      fields.value.payment_types_id.value = "";
      fields.value.chart_of_accounts_id.value = "";
    }
  }
};

const handleSelectSalesChannel = (item: any) => {
  if (item) {
    fields.value.to_deliver.value = item.require_delivery == true;
  }
  prepareItemRow();
};

const selectedOutlet = ref<Outlet | null>(null);
const selectOutlet = (outlet: Outlet) => {
  console.log("selected outlet", outlet);
  selectedOutlet.value = outlet;
  prepareItemRow();
};

const prepareItemRow = () => {
  if (
    !isEmpty(formData.value.customers_id) &&
    !isEmpty(formData.value.warehouses_id) &&
    !isEmpty(formData.value.price_lists_id) &&
    !isEmpty(formData.value.credit_terms_id)
  ) {
    if (props.cartItems) prepareCartItems();
    allowNewItemRow.value = true;
  } else {
    allowNewItemRow.value = false;
  }
};

const prepareCartItems = async () => {
  const ids = props.cartItems?.map((item) => item.id);
  const response = await api.post(
    "clients/inventory/item-management/items/search",
    {
      ids: ids,
      price_lists_id: formData.value.price_lists_id,
      warehouses_id: formData.value.warehouses_id,
    }
  );
  if (response.success == true) {
    if (itemGridRef.value) {
      itemGridRef.value.handleSelectedItems(response.data);
    }
  }
};

const activeTab = ref("items");
const activeTabMobile = ref("customer");

const assignSalesRep = () => {
  console.log("assign 11 ");
  showSelectSalesReps.value = true;
};

const createDeliveryTracking = async () => {
  let params = {
    id: formData.value.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "create-delivery-tracking",
    params
  );
  if (response.success == true) {
    setFormData(response.data);
    Toast.show("Delivery Tracking created", "info");
  } else {
    Toast.show("Could not creatre Devliery Tracking", "warning");
  }
};

function warehouseLoaded() {
  if (props.mode == "create") {
    fields.value.warehouses_id.value = tokenManager.getSetting(
      "default_warehouse",
      true
    );
  }
}
function priceListLoaded() {
  if (props.mode == "create") {
    fields.value.price_lists_id.value = tokenManager.getSetting(
      "default_price_list",
      true
    );
  }
}

const paymentTerms = ref<any[]>([]);
function creditTermsLoaded(data: any[]) {
  console.log("credit terms", data);
  paymentTerms.value = data;
  if (props.mode == "create") {
    fields.value.credit_terms_id.value = tokenManager.getSetting(
      "default_credit_term",
      true
    );
  }
  const ct = paymentTerms.value.find(
    (ct) => ct.id == fields.value.credit_terms_id.value
  );
  console.log("ct", ct);
  if (ct) {
    const pt = paymentTypes.value.find((pt) => pt.code == ct.name);
    if (pt) {
      fields.value.payment_types_id.value = pt.id;
      fields.value.chart_of_accounts_id.value = pt.chart_of_accounts_id;
    } else {
      fields.value.payment_types_id.value = "";
      fields.value.chart_of_accounts_id.value = "";
    }
  }
}

const convertDocuments = async (doc_type: string) => {
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "convert",
    {
      id: formData.value.uid,
      convert: doc_type,
    }
  );
  if (response.success == true) {
    MessageBox.confirm({
      title: doc_type + " Created",
      message: "Credit Note created successfully",
      confirmText: "Print " + doc_type,
      cancelText: "Close",
      extraButton: {
        text: "View " + doc_type,
        action: () => {
          if (doc_type == "Credit Note")
            formData.value.credit_note = response.data.data;
          viewDocument(doc_type);
        },
      },
    }).then((confirmed) => {
      if (confirmed) {
        printPDFBlob(response.data.file);
      }
    });
  }
};

const viewDocument = (doc_type: string) => {
  if (doc_type == "Credit Note" && formData.value.credit_note)
    emit(
      "viewDocument",
      formData.value.credit_note,
      "Sales Credit Note",
      "revenue/sales/sales-credit-notes"
    );
  else if (doc_type == "Goods Delivery" && formData.value.delivery)
    emit(
      "viewDocument",
      formData.value.delivery,
      "Sales Goods Delivery",
      "revenue/sales/sales-goods-deliverys"
    );
  else if (doc_type == "Sales Order" && formData.value.order)
    emit(
      "viewDocument",
      formData.value.order,
      "Sales Order",
      "revenue/sales/sales-orders"
    );
  else if (doc_type == "Sales Invoice" && formData.value.quotation)
    emit(
      "viewDocument",
      formData.value.quotation,
      "Sales Invoice",
      "revenue/sales/sales-quotations"
    );
};

const updateItems = (items: Item[] | []) => {
  formData.value.items = items;
};

const chartofaccounts = ref<ChartOfAccount[]>([]);
const getChartOfAccounts = async () => {
  const response = await api.post("finance/accounting/chart-of-accounts/list", {
    extra: "Bank,Cash",
  });
  if (response.success == true) {
    chartofaccounts.value = response.data;
  }
};

const hanldeOcrRead = (reference: string, _amount: string) => {
  if (reference) fields.value.payment_reference.value = reference;
};

const showForm = ref(false);
onMounted(() => {
  showForm.value = true;
  getChartOfAccounts();
  isReadOnly.value = props.mode == "view";
  if (props.mode != "create") {
    getRecord();
  } else {
    clearForm();
  }
});

const currency = ref<Currency>();
const currencys = ref<Currency[]>([]);
const updateExchangeRate = (curr: Currency) => {
  currency.value = curr;
  if (curr && curr.exchange_rates && curr.exchange_rates.length > 0) {
    fields.value.exchange_rate.value = curr.exchange_rates[0].rate;
  } else {
    fields.value.exchange_rate.value = 1;
  }
};

const currencyLoaded = (data: any[]) => {
  currencys.value = data;
  if (data && data.length > 0 && data[0].exchange_rates.lenght > 0) {
    currency.value = data[0];
    fields.value.currencys_id.value = data[0].id;
    fields.value.exchange_rate.value = data[0].exchange_rates[0].rate;
  } else {
    fields.value.currencys_id.value = 1;
    fields.value.exchange_rate.value = 1;
  }
};
</script>

<template>
  <ModalFormComponent width="100%" @close="$emit('close')">
    <template #header>
      <!-- Header -->
      <DocumentHeader
        :title="title"
        :end-point="endPoint"
        :data="formData"
        :readonly="isReadOnly"
        :show-drop-down="isReadOnly"
        @close="$emit('close')"
        :permissions="permissions"
        @clear="clearForm"
        @new="clearForm"
        @save="handleSubmit(false)"
        @download="handleSubmit(false, true)"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      >
        <template #dropdownbuttons>
          <button
            @click="createDeliveryTracking"
            v-if="!formData?.delivery_tracking && !formData.credit_note"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="Truck" />
            Create Tracking
          </button>
          <button
            @click="assignSalesRep"
            v-if="!formData.credit_note"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="UsersRound" />
            Assign Sales Rep
          </button>
          <hr
            v-if="!data?.sales_reps_id && !formData.credit_note"
            class="border-gray-700 dark:border-gray-200"
          />
          <button
            @click="convertDocuments('Credit Note')"
            v-if="!formData.credit_note"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="Truck" />
            Create Credit Note
          </button>
          <hr
            v-if="
              (formData.order || formData.quotation || formData.delivery) &&
              !formData.credit_note
            "
            class="border-gray-700 dark:border-gray-200"
          />
          <button
            @click="viewDocument('Credit Note')"
            v-if="formData.credit_note"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="Truck" />
            View Credit Note ({{ formData.credit_note?.document_no }})
          </button>
          <button
            @click="viewDocument('Sales Invoice')"
            v-if="formData.quotation"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="Truck" />
            View Quotation ({{ formData.quotation?.document_no }})
          </button>
          <button
            @click="viewDocument('Sales Order')"
            v-if="formData.order"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="Receipt" />
            View Sales Order ({{ formData.order?.document_no }})
          </button>
          <button
            @click="viewDocument('Goods Delivery')"
            v-if="formData.delivery"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="Truck" />
            View Goods Delivery ({{ formData.delivery?.document_no }})
          </button>
        </template>
      </DocumentHeader>
    </template>

    <template #form>
      <template v-if="!isMobile">
        <div class="grid md:grid-cols-5 gap-6 mb-8">
          <!-- Left Column -->
          <div class="space-y-6 md:col-span-3">
            <div class="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <div class="">
                  <SearchSelect
                    label="Customer"
                    end-point="revenue/master-data/customers"
                    v-model="fields.customers_id.value"
                    v-if="!isReadOnly"
                    :readonly="isReadOnly"
                    id="customers_id"
                    :remote="true"
                    :withDetails="true"
                    :allow-new-record="true"
                    :error="
                      fields.customers_id.touched
                        ? fields.customers_id.error
                        : null
                    "
                    @blur="fields.customers_id.touched = true"
                    @select="handleCustomer"
                  />
                </div>

                <div
                  v-if="customer"
                  class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    {{ customer?.name }}
                  </h4>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ customer?.email }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ customer?.mobile }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ customer?.address ?? customer?.building_address }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    <span>TIN: </span> {{ customer?.tax_registration_no }}
                    <span
                      :style="{
                        float: 'right',
                        color:
                          (Number(customer?.credit_limit) || 0) -
                            (Number(customer?.outstanding) || 0) <
                          0
                            ? 'red'
                            : 'green',
                      }"
                    >
                      Credit Balance:
                      {{
                        formatNumber(
                          (Number(customer?.credit_limit) || 0) -
                            (Number(customer?.outstanding) || 0)
                        )
                      }}
                    </span>
                  </p>
                </div>
                <div
                  v-else
                  class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 text-center"
                >
                  No Customer selected
                </div>
                <div class="md:grid md:grid-cols-2 md:gap-4">
                  <SearchSelect
                    label="Currency"
                    end-point="finance/master-data/currency"
                    v-model="fields.currencys_id.value"
                    :readonly="isReadOnly"
                    @select="updateExchangeRate"
                    @loaded="currencyLoaded"
                    :error="
                      fields.currencys_id.touched
                        ? fields.currencys_id.error
                        : null
                    "
                    @blur="fields.currencys_id.touched = true"
                  />
                  <NumberInput
                    label="Exchange Rate"
                    :readonly="isReadOnly"
                    v-model="fields.exchange_rate.value"
                    :show-controls="false"
                    :error="
                      fields.exchange_rate.touched
                        ? fields.exchange_rate.error
                        : null
                    "
                    @blur="fields.exchange_rate.touched = true"
                  />
                </div>
              </div>
              <div class="space-y-6">
                <div class="grid grid-cols-5 gap-6 mb-8">
                  <div
                    class="flex justify-between items-center mb-2 col-span-1"
                  >
                    <CheckInput
                      v-model="fields.to_deliver.value"
                      :readonly="isReadOnly"
                      label="To Deliver"
                      :error="
                        fields.to_deliver.touched
                          ? fields.to_deliver.error
                          : null
                      "
                      @blur="fields.to_deliver.touched = true"
                    />
                  </div>
                  <div class="col-span-2">
                    <DateInput
                      v-model="fields.delivery_date.value"
                      label="Delviery Date"
                      :readonly="isReadOnly"
                      :error="
                        fields.delivery_date.touched
                          ? fields.delivery_date.error
                          : null
                      "
                      @blur="fields.delivery_date.touched = true"
                    />
                  </div>
                  <div class="col-span-2">
                    <SearchSelect
                      label="Sales Rep"
                      end-point="revenue/master-data/sales-reps"
                      v-model="fields.sales_reps_id.value"
                      :readonly="isReadOnly"
                      :allow-new-record="true"
                      @select="prepareItemRow"
                      :error="
                        fields.sales_reps_id.touched
                          ? fields.sales_reps_id.error
                          : null
                      "
                      @blur="fields.sales_reps_id.touched = true"
                    />
                  </div>
                </div>
                <div class="flex justify-between items-center mb-2">
                  <div v-if="!isReadOnly" class="flex gap-2">
                    <RadioInput
                      option-label="full_address"
                      name="delivery_address"
                      label="Delivery Address"
                      :options="customer?.addresses || []"
                      v-model="fields.delivery_address_id.value"
                      :readonly="isReadOnly"
                    />
                    <textarea
                      v-model="formData.delivery_remarks"
                      :readonly="isReadOnly"
                      rows="3"
                      class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
                      placeholder="Delivery Notes"
                    ></textarea>
                  </div>
                  <div
                    v-else
                    class="w-full p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <h4 class="font-medium text-gray-900 dark:text-white">
                      Delivery Address
                    </h4>
                    {{ formData.delivery_address?.full_address }}
                    <div class="text-sm text-gray-600 dark:text-gray-400">
                      {{ formData.delivery_remarks }}
                    </div>
                  </div>
                </div>
                <div class="flex justify-between items-center mb-2" v-if="mode == 'create'">
                  <SearchSelect
                    label="Payment Type"
                    end-point="finance/master-data/payment-types"
                    v-model="fields.payment_types_id.value"
                    :readonly="isReadOnly"
                    @loaded="paymentTypesLoaded"
                    @select="handlePaymetType"
                    :error="
                      fields.payment_types_id.touched
                        ? fields.payment_types_id.error
                        : null
                    "
                    @blur="fields.payment_types_id.touched = true"
                  />
                  <SearchSelect
                    label="Bank/Cash Account"
                    v-model="fields.chart_of_accounts_id.value"
                    end-point="finance/accounting/chart-of-accounts"
                    :options="chartofaccounts"
                    extra="Bank,Cash"
                    :extra2="fields.currencys_id.value"
                    :readonly="isReadOnly"
                    :withDetails="true"
                    :error="
                      fields.chart_of_accounts_id.touched
                        ? fields.chart_of_accounts_id.error
                        : null
                    "
                    @blur="fields.chart_of_accounts_id.touched = true"
                  />
                  <div class="space-y-6 col-span-2">
                    <FileUpload
                      v-model="fields.payment_attachment.value"
                      v-if="payment_type && payment_type?.require_attachment"
                      label="Attachment"
                      path="payment_receipts"
                      :readonly="isReadOnly"
                      :ocr="payment_type.read_ocr"
                      :ocr-length="payment_type.ocr_string_length"
                      :ocr-match="payment_type.ocr_init_string"
                      :currency="currency?.code"
                      @ocrRead="hanldeOcrRead"
                      :error="
                        fields.payment_attachment.touched
                          ? fields.payment_attachment.error
                          : null
                      "
                      @blur="fields.payment_attachment.touched = true"
                    />
                    <TextInput
                      v-model="fields.payment_reference.value"
                      v-if="payment_type && payment_type?.require_reference"
                      label="Reference"
                      :readonly="isReadOnly"
                      type="textarea"
                      :error="
                        fields.payment_reference.touched
                          ? fields.payment_reference.error
                          : null
                      "
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Right Column -->
          <div class="space-y-6 col-span-2">
            <!-- Quotation Details -->
            <div class="space-y-4">
              <div class="grid grid-cols-3 gap-4">
                <div>
                  <SearchSelect
                    label="Warehouse"
                    end-point="system/company/warehouses"
                    v-model="fields.warehouses_id.value"
                    @loaded="warehouseLoaded"
                    :readonly="isReadOnly"
                    @select="handleWarehouse"
                    :error="
                      fields.warehouses_id.touched
                        ? fields.warehouses_id.error
                        : null
                    "
                    @blur="fields.warehouses_id.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Price List"
                    end-point="finance/master-data/price-lists"
                    v-model="fields.price_lists_id.value"
                    @loaded="priceListLoaded"
                    :readonly="isReadOnly"
                    @select="prepareItemRow"
                    :error="
                      fields.price_lists_id.touched
                        ? fields.price_lists_id.error
                        : null
                    "
                    @blur="fields.price_lists_id.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Outlet"
                    end-point="system/company/outlets"
                    v-model="fields.outlets_id.value"
                    :readonly="isReadOnly"
                    @select="selectOutlet"
                    :error="
                      fields.outlets_id.touched ? fields.outlets_id.error : null
                    "
                    @blur="fields.outlets_id.touched = true"
                  />
                </div>
              </div>

              <div class="grid grid-cols-3 gap-4">
                <div>
                  <SearchSelect
                    label="Payment Terms"
                    end-point="finance/master-data/credit-terms"
                    v-model="fields.credit_terms_id.value"
                    :readonly="isReadOnly"
                    @loaded="creditTermsLoaded"
                    @select="handlePaymetTerms"
                    :error="
                      fields.credit_terms_id.touched
                        ? fields.credit_terms_id.error
                        : null
                    "
                    @blur="fields.credit_terms_id.touched = true"
                  />
                </div>
                <div>
                  <DateInput
                    v-model="fields.due_date.value"
                    label="Duie Date"
                    :readonly="isReadOnly"
                    :error="
                      fields.due_date.touched ? fields.due_date.error : null
                    "
                    @blur="fields.due_date.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Sales Channel"
                    end-point="revenue/master-data/sales-channels"
                    v-model="fields.sales_channels_id.value"
                    @loaded="handleSalesChannelLoaded"
                    :readonly="isReadOnly"
                    @select="handleSelectSalesChannel"
                    :error="
                      fields.sales_channels_id.touched
                        ? fields.sales_channels_id.error
                        : null
                    "
                    @blur="fields.sales_channels_id.touched = true"
                  />
                </div>
              </div>

              <div class="grid grid-cols-3 gap-4">
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Date
                  </label>
                  <input
                    v-model="formData.date_time"
                    :readonly="isReadOnly"
                    type="date"
                    class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Customer Reference No.
                  </label>
                  <input
                    v-model="fields.customer_reference.value"
                    type="text"
                    :readonly="isReadOnly"
                    class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Document Number
                  </label>
                  <input
                    v-model="fields.ref_no.value"
                    type="text"
                    readonly
                    class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <TabComponent :tabs="mobileTabs" v-model="activeTabMobile">
          <template #customer>
            <div>
              <div class="">
                <SearchSelect
                  label="Customer"
                  end-point="revenue/master-data/customers"
                  v-if="!isReadOnly"
                  v-model="fields.customers_id.value"
                  :allow-new-record="true"
                  id="customers_id"
                  :readonly="isReadOnly"
                  :remote="true"
                  :withDetails="true"
                  :error="
                    fields.customers_id.touched
                      ? fields.customers_id.error
                      : null
                  "
                  @blur="fields.customers_id.touched = true"
                  @select="handleCustomer"
                />
              </div>

              <div
                v-if="customer"
                class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <h4 class="font-medium text-gray-900 dark:text-white">
                  {{ customer?.name }}
                </h4>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  {{ customer?.email }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  {{ customer?.mobile }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  {{ customer?.address ?? customer?.building_address }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  <span>TIN: </span> {{ customer?.tax_registration_no }}
                </p>
              </div>
              <div
                v-else
                class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 text-center"
              >
                No Customer selected
              </div>
            </div>
          </template>

          <template #delivery>
            <div class="space-y-6">
              <div class="grid grid-cols-3 gap-6 mb-8">
                <div class="flex justify-between items-center mb-2 col-span-1">
                  <CheckInput
                    v-model="fields.to_deliver.value"
                    :readonly="isReadOnly"
                    label="To Deliver"
                    :error="
                      fields.to_deliver.touched ? fields.to_deliver.error : null
                    "
                    @blur="fields.to_deliver.touched = true"
                  />
                </div>
                <div class="col-span-2">
                  <DateInput
                    v-model="fields.delivery_date.value"
                    label="Delviery Date"
                    :readonly="isReadOnly"
                    :error="
                      fields.delivery_date.touched
                        ? fields.delivery_date.error
                        : null
                    "
                    @blur="fields.delivery_date.touched = true"
                  />
                </div>
              </div>
              <div class="flex justify-between items-center mb-2">
                <div v-if="!isReadOnly">
                  <RadioInput
                    option-label="full_address"
                    name="delivery_address"
                    label="Delivery Address"
                    :options="customer?.addresses || []"
                    v-model="fields.delivery_address_id.value"
                    :readonly="isReadOnly"
                  />
                  <textarea
                    v-model="formData.delivery_remarks"
                    :readonly="isReadOnly"
                    rows="3"
                    class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 mt-1"
                    placeholder="Delivery Notes"
                  ></textarea>
                </div>
                <div
                  v-else
                  class="w-full p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    Delivery Address
                  </h4>
                  {{ formData.delivery_address?.full_address }}
                </div>
              </div>
            </div>
          </template>
          <template #document>
            <div class="space-y-4">
              <div class="grid md:grid-cols-3 gap-4">
                <div>
                  <SearchSelect
                    label="Warehouse"
                    end-point="system/company/warehouses"
                    v-model="fields.warehouses_id.value"
                    @loaded="warehouseLoaded"
                    :readonly="isReadOnly"
                    @select="handleWarehouse"
                    :error="
                      fields.warehouses_id.touched
                        ? fields.warehouses_id.error
                        : null
                    "
                    @blur="fields.warehouses_id.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Price List"
                    end-point="finance/master-data/price-lists"
                    v-model="fields.price_lists_id.value"
                    @loaded="priceListLoaded"
                    :readonly="isReadOnly"
                    @select="prepareItemRow"
                    :error="
                      fields.price_lists_id.touched
                        ? fields.price_lists_id.error
                        : null
                    "
                    @blur="fields.price_lists_id.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Payment Terms"
                    end-point="finance/master-data/credit-terms"
                    v-model="fields.credit_terms_id.value"
                    @loaded="creditTermsLoaded"
                    :readonly="isReadOnly"
                    @select="handlePaymetTerms"
                    :error="
                      fields.credit_terms_id.touched
                        ? fields.credit_terms_id.error
                        : null
                    "
                    @blur="fields.credit_terms_id.touched = true"
                  />
                </div>
              </div>

              <div class="grid md:grid-cols-3 gap-4">
                <div>
                  <SearchSelect
                    label="Outlet"
                    end-point="system/company/outlets"
                    v-model="fields.outlets_id.value"
                    :readonly="isReadOnly"
                    @select="prepareItemRow"
                    :error="
                      fields.outlets_id.touched ? fields.outlets_id.error : null
                    "
                    @blur="fields.outlets_id.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Sales Rep"
                    end-point="revenue/master-data/sales-reps"
                    v-model="fields.sales_reps_id.value"
                    :readonly="isReadOnly"
                    :allow-new-record="true"
                    @select="prepareItemRow"
                    :error="
                      fields.sales_reps_id.touched
                        ? fields.sales_reps_id.error
                        : null
                    "
                    @blur="fields.sales_reps_id.touched = true"
                  />
                </div>
                <div>
                  <SearchSelect
                    label="Sales Channel"
                    end-point="revenue/master-data/sales-channels"
                    v-model="fields.sales_channels_id.value"
                    @loaded="handleSalesChannelLoaded"
                    :readonly="isReadOnly"
                    @select="prepareItemRow"
                    :error="
                      fields.sales_channels_id.touched
                        ? fields.sales_channels_id.error
                        : null
                    "
                    @blur="fields.sales_channels_id.touched = true"
                  />
                </div>
              </div>

              <div class="grid md:grid-cols-3 gap-4">
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Customer Reference No.
                  </label>
                  <input
                    v-model="fields.customer_reference.value"
                    type="text"
                    :readonly="isReadOnly"
                    class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Date
                  </label>
                  <input
                    v-model="formData.date_time"
                    :readonly="isReadOnly"
                    type="date"
                    class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Document Number
                  </label>
                  <input
                    v-model="fields.ref_no.value"
                    type="text"
                    readonly
                    class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </div>
              </div>
            </div>
          </template>
        </TabComponent>
      </template>
      <div
        class="space-y-6"
        v-if="isReadOnly && !isEmpty(formData.delivery_tracking)"
      >
        <DeliverySteps
          :data="formData.delivery_tracking"
          :record-status="recordStatus || []"
        />
      </div>
      <!-- Tabs -->
      <div class="mb-6" v-if="!isMobile">
        <TabComponent
          :tabs="[
            { name: 'items', label: 'Items', visible: true },
            {
              name: 'payments',
              label: 'Payments',
              visible: isReadOnly && formData.payments.length > 0,
            },
            {
              name: 'accounting',
              label: 'Accounting',
              visible: isReadOnly,
            },
          ]"
          v-model="activeTab"
        >
          <template #buttons>
            <button
              @click="handleNewItemRow"
              v-if="!isReadOnly && !isMobile"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              Add Item
            </button>
          </template>
          <template #items>
            <!-- TODO: 
                 Allow discount and change price to be dyamic from permissions
                 -->
            <ItemSalesGrid
              ref="itemGridRef"
              :items="formData.items"
              :readonly="isReadOnly"
              :customer="customer"
              :warehouses_id="formData.warehouses_id"
              :outlet="selectedOutlet"
              :currency="currency"
              :exchange_rate="formData.exchange_rate"
              :price_lists_id="formData.price_lists_id"
              :allow-change-price="
                (permissions || []).some((p) => p.permission == 'Change Price')
              "
              :allow-discount="
                (permissions || []).some(
                  (p) => p.permission == 'Allow Discount'
                )
              "
              :permissions="permissions"
              :allow-new-row="allowNewItemRow"
              :shelf-managed="warehouse?.shelf_managed ?? false"
              :shelfs="warehouse?.shelfs"
              @items="updateItems"
            />
            <!-- Terms and Notes -->
            <div class="grid md:grid-cols-2 gap-6">
              <div>
                <div>
                  <TextAreaInput
                    label="Remarks"
                    v-model="formData.remarks"
                    :readonly="isReadOnly"
                    :rows="7"
                    placeholder="Additional notes or remarks"
                  ></TextAreaInput>
                </div>
              </div>
              <div>
                <TextAreaInput
                  label="Internal Notes"
                  v-model="formData.remarks"
                  :rows="7"
                  :readonly="isReadOnly"
                  placeholder="Internal Notes"
                />
              </div>
            </div>
          </template>
          <template #payments>
            <Payments :data="formData.payments" v-if="isReadOnly" />
          </template>
          <template #accounting>
            <AccountingLog :data="formData.accounting" v-if="isReadOnly" />
          </template>
        </TabComponent>
      </div>
      <template v-if="isMobile">
        <ItemSalesGrid
          ref="itemGridRef"
          :items="formData.items"
          :readonly="isReadOnly"
          :customer="customer"
          :exchange_rate="formData.exchange_rate"
          :currency="currency"
          :warehouses_id="formData.warehouses_id"
          :price_lists_id="formData.price_lists_id"
          :allow-change-price="false"
          :allow-discount="true"
          :allow-new-row="allowNewItemRow"
          :shelf-managed="warehouse?.shelf_managed ?? false"
          :shelfs="warehouse?.shelfs"
          @items="updateItems"
        />
        <h3 class="mb-2" v-if="isReadOnly">Accounting</h3>
        <AccountingLog :data="formData.accounting" v-if="isReadOnly" />
      </template>
    </template>

    <!-- Footer -->
    <template #footer>
      <DocumentFooter
        :data="formData"
        :readonly="isReadOnly"
        :end-point="endPoint"
        :permissions="permissions"
        :title="title"
        convert-module="Debit Note"
        @close="$emit('close')"
        @save="handleSubmit(false)"
        @save-new="handleSubmit(true)"
        @download="handleSubmit(false, true)"
        @new="clearForm"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      />
      <SelectSalesReps
        v-if="showSelectSalesReps"
        @close="showSelectSalesReps = false"
        @select="updateSalesRep"
      />
    </template>
  </ModalFormComponent>
</template>
